import React, { useState, useEffect } from 'react';
import { Plus, PencilSimple, Power, CalendarCheck, Money, CaretDown, CaretUp, Copy } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND, formatDate, toYmd } from '_helpers';
import { PageHeader, Bezel, Table, Button, IconButton, Modal, Input, Tag, StatCard, MotionReveal, Spinner, cn } from '_ui';

function methodLabel(m) {
    return m === 'cash' ? 'Tiền mặt' : 'Chuyển khoản';
}

export function LopHoc() {
    const { data, loading, refetch } = useFetch('/api/classes');
    const api = useApi();
    const alert = useAlertActions();
    const [editing, setEditing] = useState(null); // class | {} (new) | null
    const [showInactive, setShowInactive] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const classes = data || [];
    const activeCount = classes.filter(c => c.active).length;
    const rows = classes.filter(c => showInactive || c.active);

    async function toggleActive(cls) {
        const res = await api.patch(`/api/classes/${cls._id}`, { active: !cls.active });
        if (res.ok) { alert.success(cls.active ? 'Đã đóng lớp' : 'Đã mở lại lớp'); refetch(); }
        else alert.error('Không thể cập nhật', res.error);
    }

    const columns = [
        { key: 'name', header: 'Lớp học', render: c => <span className={c.active ? 'font-medium text-espresso' : 'text-espresso-soft/60 line-through'}>{c.name}</span> },
        { key: 'sessions', header: 'Số buổi', align: 'right', render: c => <span className="tnum">{c.totalSessions}</span> },
        { key: 'students', header: 'Học viên', align: 'right', render: c => <span className="tnum">{c.studentCount}</span> },
        { key: 'status', header: 'Trạng thái', align: 'center', render: c => <Tag tone={c.active ? 'sage' : 'neutral'}>{c.active ? 'Đang mở' : 'Đã đóng'}</Tag> },
        { key: 'actions', header: '', align: 'right', render: c => (
            <div className="flex justify-end gap-1">
                <IconButton icon={PencilSimple} label="Sửa" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(c); }} />
                <IconButton icon={Power} label={c.active ? 'Đóng lớp' : 'Mở lại'} variant="ghost"
                    className={c.active ? 'hover:text-danger' : 'hover:text-sage-deep'} onClick={(e) => { e.stopPropagation(); toggleActive(c); }} />
            </div>
        ) },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Lớp nail"
                title="Lớp học"
                description="Quản lý các lớp, sĩ số và số buổi học. Bấm vào một lớp để xem danh sách học viên, điểm danh và học phí."
                actions={
                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setShowInactive(v => !v)}>
                            {showInactive ? 'Ẩn lớp đã đóng' : 'Hiện lớp đã đóng'}
                        </Button>
                        <Button icon={Plus} onClick={() => setEditing({})}>Thêm lớp</Button>
                    </div>
                }
            />

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Đang mở" value={activeCount} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Tổng số lớp" value={classes.length} /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={rows} loading={loading} onRowClick={c => setSelectedId(c._id)}
                    empty={showInactive ? 'Chưa có lớp học nào. Bấm “Thêm lớp”.' : 'Không có lớp đang mở.'} />
            </Bezel>

            {editing && <ClassModal cls={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
            {selectedId && <ClassDetailModal classId={selectedId} onClose={() => setSelectedId(null)} onChanged={refetch} />}
        </div>
    );
}

function ClassModal({ cls, onClose, onSaved }) {
    const isEdit = !!cls._id;
    const api = useApi();
    const alert = useAlertActions();
    const [form, setForm] = useState({ name: cls.name || '', totalSessions: cls.totalSessions ?? '' });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

    function validate() {
        const e = {};
        if (!form.name.trim()) e.name = 'Nhập tên lớp';
        const n = Number(form.totalSessions);
        if (form.totalSessions === '' || n <= 0) e.totalSessions = 'Số buổi phải lớn hơn 0';
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function save() {
        if (!validate()) return;
        setSaving(true);
        const body = { name: form.name.trim(), totalSessions: Number(form.totalSessions) };
        const res = isEdit
            ? await api.patch(`/api/classes/${cls._id}`, body)
            : await api.post('/api/classes', body);
        setSaving(false);
        if (res.ok) { alert.success(isEdit ? 'Đã lưu lớp học' : 'Đã thêm lớp học'); onSaved(); }
        else alert.error('Không thể lưu', res.error);
    }

    return (
        <Modal open onClose={onClose} eyebrow="Lớp học" title={isEdit ? 'Sửa lớp học' : 'Thêm lớp học'}
            footer={<><Button variant="ghost" onClick={onClose}>Huỷ</Button><Button onClick={save} loading={saving}>Lưu</Button></>}>
            <div className="space-y-5">
                <Input label="Tên lớp" value={form.name} onChange={e => set('name', e.target.value)} error={errors.name} placeholder="VD: Nail cơ bản K12" />
                <Input label="Tổng số buổi học" type="number" min="1" value={form.totalSessions} onChange={e => set('totalSessions', e.target.value)} error={errors.totalSessions} placeholder="20" />
            </div>
        </Modal>
    );
}

function ClassDetailModal({ classId, onClose, onChanged }) {
    const { data, loading, refetch } = useFetch(`/api/classes/${classId}`);

    function refreshAll() { refetch(); onChanged(); }

    return (
        <Modal open onClose={onClose} size="lg" eyebrow="Lớp học" title={data?.class?.name || (loading ? 'Đang tải…' : 'Lớp học')}>
            {loading ? (
                <div className="flex justify-center py-10"><Spinner /></div>
            ) : !data ? (
                <p className="py-6 text-center text-sm text-espresso-soft/70">Không tải được dữ liệu lớp.</p>
            ) : (
                <div className="space-y-8">
                    <AttendanceBlock classId={classId} students={data.students} totalSessions={data.class.totalSessions} onSaved={refreshAll} />
                    <Roster students={data.students} totalSessions={data.class.totalSessions} onPaid={refreshAll} />
                </div>
            )}
        </Modal>
    );
}

function AttendanceBlock({ classId, students, totalSessions, onSaved }) {
    const [date, setDate] = useState(toYmd(new Date()));
    const { data, loading, refetch } = useFetch(`/api/classes/${classId}/attendance?date=${date}`);
    const [checked, setChecked] = useState(new Set());
    const api = useApi();
    const alert = useAlertActions();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setChecked(new Set(data?.presentStudentIds || []));
    }, [data]);

    function toggle(id) {
        setChecked(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    async function save() {
        setSaving(true);
        const res = await api.post(`/api/classes/${classId}/attendance`, { date, presentStudentIds: [...checked] });
        setSaving(false);
        if (res.ok) { alert.success('Đã lưu điểm danh'); refetch(); onSaved(); }
        else alert.error('Không thể lưu điểm danh', res.error);
    }

    const roster = (students || []).filter(s => s.enrollmentActive);

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 font-serif text-xl text-espresso">
                    <CalendarCheck size={20} weight="light" /> Điểm danh
                </h3>
                <div className="flex items-center gap-3">
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        className="rounded-2xl bg-cream-200/60 px-4 py-2 text-sm ring-1 ring-espresso/10 focus:bg-cream-50 focus:outline-none focus:ring-2 focus:ring-sage/60" />
                    <Button size="sm" onClick={save} loading={saving}>Lưu điểm danh</Button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-6"><Spinner size={22} /></div>
            ) : roster.length === 0 ? (
                <p className="mt-4 text-sm text-espresso-soft/70">Chưa có học viên đang học trong lớp.</p>
            ) : (
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {roster.map(s => (
                        <label key={s.enrollmentId} className="flex cursor-pointer items-center gap-3 rounded-xl bg-cream-200/40 px-4 py-2.5 ring-1 ring-espresso/[0.06]">
                            <input type="checkbox" className="h-4 w-4 accent-sage" checked={checked.has(s.student._id)} onChange={() => toggle(s.student._id)} />
                            <span className="text-sm text-espresso">{s.student.name}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function Roster({ students, totalSessions, onPaid }) {
    return (
        <div>
            <h3 className="mb-4 font-serif text-xl text-espresso">Danh sách học viên</h3>
            {(!students || students.length === 0) ? (
                <p className="text-sm text-espresso-soft/70">Chưa có học viên trong lớp này.</p>
            ) : (
                <div className="space-y-3">
                    {students.map(en => <StudentRow key={en.enrollmentId} enrollment={en} totalSessions={totalSessions} onPaid={onPaid} />)}
                </div>
            )}
        </div>
    );
}

function StudentRow({ enrollment: en, totalSessions, onPaid }) {
    const [expanded, setExpanded] = useState(false);
    const [paying, setPaying] = useState(false);
    const alert = useAlertActions();
    const remaining = en.remaining;
    const settled = remaining <= 0;

    function copyCode() {
        if (navigator.clipboard) navigator.clipboard.writeText(en.tuitionCode).then(() => alert.success('Đã sao chép mã'));
    }

    return (
        <div className="rounded-2xl bg-cream-200/50 p-4 ring-1 ring-espresso/10">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <span className={cn('font-medium', en.enrollmentActive ? 'text-espresso' : 'text-espresso-soft/60')}>{en.student.name}</span>
                        {!en.enrollmentActive && <Tag tone="neutral">Đã nghỉ</Tag>}
                    </div>
                    <button onClick={copyCode} className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs text-espresso-soft transition-colors hover:text-espresso">
                        {en.tuitionCode} <Copy size={12} weight="light" />
                    </button>
                </div>
                <div className="text-right text-sm">
                    <div className="text-espresso-soft/70">Điểm danh <span className="tnum text-espresso">{en.attendanceCount}/{totalSessions}</span></div>
                    <div className="mt-0.5 tnum text-espresso-soft/70">Đã đóng {formatVND(en.paidTotal)} / {formatVND(en.tuitionPrice)}</div>
                    <div className={cn('mt-0.5 tnum font-semibold', settled ? 'text-sage-deep' : 'text-danger')}>
                        {settled ? 'Đã đóng đủ' : `Còn lại ${formatVND(remaining)}`}
                    </div>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4">
                <Button size="sm" variant="soft" icon={Money} onClick={() => setPaying(v => !v)}>Thu học phí</Button>
                {en.payments?.length > 0 && (
                    <button onClick={() => setExpanded(v => !v)} className="inline-flex items-center gap-1 text-sm font-medium text-espresso-soft transition-colors hover:text-espresso">
                        Lịch sử đóng ({en.payments.length}) {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </button>
                )}
            </div>

            {paying && (
                <PaymentForm enrollmentId={en.enrollmentId} onCancel={() => setPaying(false)} onDone={() => { setPaying(false); onPaid(); }} />
            )}

            {expanded && en.payments?.length > 0 && (
                <div className="mt-3 space-y-1.5 rounded-xl bg-cream-50 p-3 ring-1 ring-espresso/[0.06]">
                    {en.payments.map(p => (
                        <div key={p._id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                            <span className="text-espresso-soft/70">{formatDate(p.paidAt, true)} · {methodLabel(p.method)}{p.note ? ` · ${p.note}` : ''}</span>
                            <span className="tnum font-medium text-espresso">{formatVND(p.amount)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function PaymentForm({ enrollmentId, onCancel, onDone }) {
    const api = useApi();
    const alert = useAlertActions();
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    async function submit() {
        if (!amount || Number(amount) <= 0) { alert.error('Số tiền không hợp lệ'); return; }
        setSaving(true);
        const res = await api.post(`/api/enrollments/${enrollmentId}/payments`, { amount: Number(amount), note: note.trim() || undefined });
        setSaving(false);
        if (res.ok) { alert.success('Đã ghi nhận thu học phí'); onDone(); }
        else alert.error('Không thể ghi nhận', res.error);
    }

    return (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-xl bg-cream-50 p-3 ring-1 ring-espresso/[0.06]">
            <Input label="Số tiền (VND)" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="min-w-[10rem] flex-1" />
            <Input label="Ghi chú (tuỳ chọn)" value={note} onChange={e => setNote(e.target.value)} className="min-w-[10rem] flex-1" />
            <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={onCancel}>Huỷ</Button>
                <Button size="sm" onClick={submit} loading={saving}>Xác nhận</Button>
            </div>
        </div>
    );
}
